import React, { useEffect, useState, useCallback } from "react";
import { Table, PageHeader, Pagination, StatCard } from "../../components/ui";
import { walletsApi, usersApi } from "../../services/api";

/* ─── Pagination Bar ─── */
function PaginationBar({ page, totalPages, totalElements, onChange }) {
  if (totalPages <= 1 || totalElements === 0) return null;

  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const pages = [];
    if (page <= 3) {
      pages.push(0, 1, 2, 3, 4, "...", totalPages - 1);
    } else if (page >= totalPages - 4) {
      pages.push(0, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
    } else {
      pages.push(0, "...", page - 1, page, page + 1, "...", totalPages - 1);
    }
    return pages;
  };

  const btnStyle = (isActive, isDisabled) => ({
    minWidth: 34, height: 34, padding: "0 10px",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    border: isActive ? "none" : "1px solid var(--border)",
    borderRadius: 8,
    background: isActive ? "#0F3D3E" : "transparent",
    color: isActive ? "#fff" : isDisabled ? "var(--text-muted)" : "var(--text-primary)",
    fontSize: 13, fontWeight: isActive ? 700 : 500,
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.45 : 1,
    transition: "all 0.15s",
    fontFamily: "var(--font-body)",
  });

  return (
    <div style={{
      padding: "14px 20px",
      borderTop: "1px solid var(--border)",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    }}>
      <button disabled={page === 0} onClick={() => onChange(page - 1)} style={btnStyle(false, page === 0)}>
        ← Prev
      </button>
      {getPages().map((pg, i) =>
        pg === "..." ? (
          <span key={`dots-${i}`} style={{ padding: "0 4px", color: "var(--text-muted)", fontSize: 14, userSelect: "none" }}>…</span>
        ) : (
          <button
            key={pg}
            onClick={() => onChange(pg)}
            style={btnStyle(page === pg, false)}
            onMouseEnter={e => { if (page !== pg) e.currentTarget.style.background = "rgba(15,61,62,0.08)"; }}
            onMouseLeave={e => { if (page !== pg) e.currentTarget.style.background = "transparent"; }}
          >
            {pg + 1}
          </button>
        )
      )}
      <button disabled={page >= totalPages - 1} onClick={() => onChange(page + 1)} style={btnStyle(false, page >= totalPages - 1)}>
        Next →
      </button>
    </div>
  );
}

/* ─── Shared User Cell renderer ─── */
function UserCell({ mongoId, userMap }) {
  const info = userMap[mongoId];
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
        {info
          ? info.name
          : <span style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-muted)" }}>{mongoId?.slice(0, 8)}…</span>
        }
      </div>
      {info?.userId && info.userId !== "—" && (
        <span style={{
          fontSize: 10, fontWeight: 700, fontFamily: "monospace",
          color: "#0F3D3E", background: "rgba(15,61,62,0.08)",
          border: "1px solid rgba(15,61,62,0.15)",
          borderRadius: 20, padding: "1px 8px", marginTop: 3,
          display: "inline-block", letterSpacing: "0.3px",
        }}>
          {info.userId}
        </span>
      )}
    </div>
  );
}

/* ─── Helper: batch-resolve userIds from API ─── */
function resolveUsers(contentArray, setUserMap) {
  const uids = [...new Set((contentArray || []).map(r => r.userId).filter(Boolean))];
  uids.forEach(uid => {
    usersApi.getById(uid)
      .then(r => {
        const u = r.data.data;
        setUserMap(p => ({
          ...p,
          [uid]: {
            name: u?.name || u?.mobile || uid.slice(0, 8) + "…",
            userId: u?.userId || "—",
          },
        }));
      })
      .catch(() => setUserMap(p => ({
        ...p,
        [uid]: { name: uid.slice(0, 8) + "…", userId: "—" },
      })));
  });
}

/* ─── Wallet Ledger Tab (CREDIT + DEBIT) ─── */
function WalletLedgerTab() {
  const [stats,   setStats]   = useState(null);
  const [data,    setData]    = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [page,    setPage]    = useState(0);
  const [loading, setLoading] = useState(true);
  const [userMap, setUserMap] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        walletsApi.getStats(),
        walletsApi.getAllTransactions(page),
      ]);
      setStats(s.data.data);
      const txData = t.data.data;
      setData(txData);
      resolveUsers(txData.content, setUserMap);
    } catch {}
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: "id", label: "Sr.",
      render: (v) => {
        const index = data.content.findIndex(item => item.id === v);
        return (
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", minWidth: 28, display: "inline-block" }}>
            {page * 20 + index + 1}
          </span>
        );
      },
    },
    {
      key: "createdAt", label: "Date",
      render: v => (
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
          {v ? new Date(v).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
        </span>
      ),
    },
    {
      key: "userId", label: "User",
      render: v => {
        const info = userMap[v];
        return (
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
            {info
              ? info.name
              : <span style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-muted)" }}>{v?.slice(0,8)}…</span>
            }
          </span>
        );
      },
    },
    {
      key: "userId", label: "User ID",
      render: v => {
        const info = userMap[v];
        return info?.userId && info.userId !== "—" ? (
          <span style={{
            fontSize: 11, fontWeight: 700, fontFamily: "monospace",
            color: "#0F3D3E", background: "rgba(15,61,62,0.08)",
            border: "1px solid rgba(15,61,62,0.15)",
            borderRadius: 20, padding: "2px 10px",
            display: "inline-block", letterSpacing: "0.3px",
          }}>
            {info.userId}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>—</span>
        );
      },
    },
    {
      key: "transactionType", label: "Type",
      render: v => (
        <span style={{ color: v === "CREDIT" ? "var(--green)" : "var(--red)", fontWeight: 700, fontSize: 13 }}>
          {v}
        </span>
      ),
    },
    {
      key: "credits", label: "Credits",
      render: v => (
        <span style={{ color: v < 0 ? "var(--red)" : "var(--green)", fontWeight: 700, fontSize: 14 }}>
          {Math.abs(Number(v))}
        </span>
      ),
    },
    {
      key: "balanceAfter", label: "Balance After",
      render: v => <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{v}</span>,
    },
    {
      key: "source", label: "Source",
      render: v => <span style={{ fontWeight: 600, fontSize: 13, color: "#0F3D3E" }}>{v}</span>,
    },
    {
      key: "description", label: "Description",
      render: v => <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{v}</span>,
    },
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 14, marginBottom: 28 }}>
        <StatCard title="Total Wallets" value={stats?.totalWallets ?? "—"}       gradient="linear-gradient(135deg,rgba(15,61,62,0.55),rgba(15,61,62,0.25))"   loading={loading} />
        <StatCard title="Total Balance" value={stats?.totalBalance != null ? stats.totalBalance.toLocaleString() : "—"} gradient="linear-gradient(135deg,rgba(208,179,106,0.6),rgba(208,179,106,0.25))" sub="Credits across all wallets" loading={loading} />
        <StatCard title="With Balance"  value={stats?.walletsWithBalance ?? "—"} gradient="linear-gradient(135deg,rgba(34,197,94,0.55),rgba(34,197,94,0.25))"  loading={loading} />
        <StatCard title="Credit Tx"     value={stats?.totalCreditTx ?? "—"}      gradient="linear-gradient(135deg,rgba(34,197,94,0.45),rgba(34,197,94,0.2))"   loading={loading} />
        <StatCard title="Debit Tx"      value={stats?.totalDebitTx ?? "—"}       gradient="linear-gradient(135deg,rgba(239,68,68,0.5),rgba(239,68,68,0.2))"    loading={loading} />
      </div>

      <div style={{ background: "#fff", border: "1px solid rgba(15,61,62,0.12)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(15,61,62,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>All Entries</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>{data.totalElements} total records</span>
        </div>
        {loading
          ? <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
          : <Table columns={columns} data={data.content} emptyText="No entries found" />
        }
        <div style={{ padding: "0 16px" }}>
          <PaginationBar page={page} totalPages={data.totalPages} totalElements={data.totalElements} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}

/* ─── Transactions Tab (DEBIT only) ─── */
function TransactionsTab() {
  const [data,    setData]    = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [page,    setPage]    = useState(0);
  const [loading, setLoading] = useState(true);
  const [userMap, setUserMap] = useState({});

  const load = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const t = await walletsApi.getAllTransactions(pg, 20, "DEBIT");
      const txData = t.data.data;
      setData(txData);
      resolveUsers(txData.content, setUserMap);
    } catch {}
    setLoading(false);
  }, [page]);

  useEffect(() => { load(page); }, [page]);

  const columns = [
    {
      key: "id", label: "Sr.",
      render: (v) => {
        const index = data.content.findIndex(item => item.id === v);
        return (
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", minWidth: 28, display: "inline-block" }}>
            {page * 20 + index + 1}
          </span>
        );
      },
    },
    {
      key: "createdAt", label: "Date",
      render: v => (
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
          {v ? new Date(v).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
        </span>
      ),
    },
    {
      key: "userId", label: "User",
      render: v => {
        const info = userMap[v];
        return (
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
            {info
              ? info.name
              : <span style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-muted)" }}>{v?.slice(0,8)}…</span>
            }
          </span>
        );
      },
    },
    {
      key: "userId", label: "User ID",
      render: v => {
        const info = userMap[v];
        return info?.userId && info.userId !== "—" ? (
          <span style={{
            fontSize: 11, fontWeight: 700, fontFamily: "monospace",
            color: "#0F3D3E", background: "rgba(15,61,62,0.08)",
            border: "1px solid rgba(15,61,62,0.15)",
            borderRadius: 20, padding: "2px 10px",
            display: "inline-block", letterSpacing: "0.3px",
          }}>
            {info.userId}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>—</span>
        );
      },
    },
    {
      key: "credits", label: "Amount Debited",
      render: v => (
        <span style={{ color: "var(--red)", fontWeight: 700, fontSize: 14 }}>{Math.abs(Number(v))}</span>
      ),
    },
    {
      key: "balanceAfter", label: "Balance After",
      render: v => <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{v}</span>,
    },
    {
      key: "source", label: "Service",
      render: v => <span style={{ fontWeight: 600, fontSize: 13, color: "#0F3D3E" }}>{v}</span>,
    },
    {
      key: "description", label: "Description",
      render: v => <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{v}</span>,
    },
  ];

  return (
    <div style={{ background: "#fff", border: "1px solid rgba(15,61,62,0.12)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(15,61,62,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Debit Transactions</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>{data.totalElements} total records</span>
      </div>
      {loading
        ? <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
        : <Table columns={columns} data={data.content} emptyText="No debit transactions found" />
      }
      <PaginationBar page={page} totalPages={data.totalPages} totalElements={data.totalElements} onChange={setPage} />
    </div>
  );
}

/* ─── Main Wallets Page ─── */
export default function Wallets() {
  const [activeTab, setActiveTab] = useState("ledger");

  return (
    <div>
      <div style={{ marginBottom: 0 }}>
        <h2 style={{
          fontFamily: "var(--font-heading, serif)",
          fontSize: 22, fontWeight: 700,
          color: "var(--text-primary, #1a1a1a)",
          marginBottom: 4,
          textTransform: "capitalize",
        }}>
          Wallets & Transactions
        </h2>
        <p style={{ fontSize: 13, color: "var(--text-muted, #888)", marginBottom: 0 }}>
          Monitor all user wallet activity
        </p>
      </div>

      <div style={{
        display: "flex", gap: 0,
        borderBottom: "2px solid #e0e0e0",
        marginTop: 18, marginBottom: 24,
      }}>
        {[
          { key: "ledger",       label: "Wallet Ledger" },
          { key: "transactions", label: "Transactions"  },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 28px", fontSize: 14, fontWeight: 600,
              background: "none", border: "none",
              borderBottom: activeTab === tab.key ? "3px solid #0F3D3E" : "3px solid transparent",
              marginBottom: -2,
              color: activeTab === tab.key ? "#0F3D3E" : "#9ca3af",
              cursor: "pointer", transition: "all 0.15s ease",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "ledger"       && <WalletLedgerTab />}
      {activeTab === "transactions" && <TransactionsTab />}
    </div>
  );
}